/**
 * A lista desenhada como cards.
 *
 * É a mesma lista da tabela — mesmas linhas, mesmas métricas, mesmas regras: ausente é "—" e nunca
 * zero, e taxa recalculada a partir de totais somados leva a marca "*". O que muda é a forma, e
 * quem escolhe é o "Exibir como" do Personalizar.
 *
 * O card se monta por blocos, na ordem de `CARD_BLOCKS`: identificação, métricas, funil de
 * entrega, chips de taxa e o bloco próprio da tela. Com "Separar por origem" ligado — a mesma
 * chave que agrupa as colunas da tabela — as métricas viram uma faixa por bloco.
 */
import type { Fmtr } from '../lib/format';
import type { CardSpec, MetricHolder } from '../lib/cards';
import { c, v } from '@/components/v2/theme';
import { BLOCK_HUE, BLOCK_LABEL, BLOCKS, M, readMetric } from '../lib/metrics';
import { CARD_BLOCKS, CARD_SIZES, type CardBlock, type ScreenPrefs } from '../lib/prefs';

/** O funil de entrega: as quatro contagens que se comparam com o enviado. */
const FUNNEL_KEYS = ['sent', 'delivered', 'uniqueOpens', 'uniqueClicks'];
const RATE_KEYS = ['deliveryRate', 'openRate', 'clickRate'];

export function MetricCards<R>({ rows, card, p, fmt, rowKey, flush, onPick }: {
    rows: readonly R[];
    card: CardSpec<R>;
    p: ScreenPrefs;
    fmt: Fmtr;
    rowKey: (row: R, i: number) => string;
    /** Clique no card: abre o detalhe da linha, como o clique na linha da tabela. */
    onPick?: (row: R) => void;
    /** Sem respiro em volta: o espaço é de quem chama (a pré-visualização, a lista aninhada).
     *  A grade continua a mesma da tela — é dela que sai o efeito do tamanho do card. */
    flush?: boolean;
}) {
    const size = CARD_SIZES.find((s) => s.k === p.cardSize) ?? CARD_SIZES[1];
    const blocks = CARD_BLOCKS.filter((b) => p.cardBlocks.includes(b));
    const keys = p.cardMetrics.filter((k) => M[k]);
    return (
        <div style={{
            display: 'grid', gap: 14, padding: flush ? 0 : 18, minWidth: 0,
            gridTemplateColumns: `repeat(auto-fill,minmax(min(100%,${size.min}px),1fr))`,
        }}>
            {rows.map((row, i) => (
                <Card key={rowKey(row, i)} row={row} card={card} blocks={blocks} keys={keys} grouped={!!p.grouped} fmt={fmt} onPick={onPick} />
            ))}
        </div>
    );
}

function Card<R>({ row, card, blocks, keys, grouped, fmt, onPick }: {
    row: R; card: CardSpec<R>; blocks: readonly CardBlock[]; keys: string[]; grouped: boolean; fmt: Fmtr;
    onPick?: (row: R) => void;
}) {
    const holder = card.pick(row);
    const derived = !!card.derived?.(row);
    const has = (b: CardBlock) => blocks.includes(b);
    return (
        <article
            {...(onPick ? { onClick: () => onPick(row), className: 'rv-h-bd', title: 'Abrir o detalhe desta linha' } : {})}
            style={{
                border: `1px solid ${v('border')}`, borderRadius: 14, background: v('surface'),
                padding: 14, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0,
                ...(onPick ? { cursor: 'pointer' } : {}),
            }}>
            {has('ident') && (
                <header style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, minWidth: 0 }}>{card.title(row)}</div>
                    {card.sub && <div style={{ fontSize: 11, color: v('faint'), minWidth: 0 }}>{card.sub(row)}</div>}
                </header>
            )}
            {has('metrics') && keys.length > 0 && (grouped
                ? <Bands keys={keys} holder={holder} derived={derived} fmt={fmt} />
                : <Grid keys={keys} holder={holder} derived={derived} fmt={fmt} />)}
            {has('funnel') && <FunnelBars holder={holder} fmt={fmt} />}
            {has('rates') && <RateChips holder={holder} derived={derived} fmt={fmt} />}
            {has('extra') && card.extra && <div style={{ minWidth: 0 }}>{card.extra(row)}</div>}
        </article>
    );
}

/* ----------------------------------------------------------------- um número */

function Value({ k, holder, derived, fmt, size }: {
    k: string; holder: MetricHolder; derived: boolean; fmt: Fmtr; size: number;
}) {
    const met = M[k];
    const value = readMetric(holder, k);
    // só taxa tem o que recalcular; sem valor não há marca a pôr
    const mark = met.block === 'rates' && value != null && derived;
    return (
        <span className="rv-mono" style={{
            fontSize: size, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
            color: value == null ? v('faint') : v('text'), whiteSpace: 'nowrap',
        }}>
            {fmt.metric(met, value)}
            {mark ? <abbr title="Recalculada a partir dos totais somados" style={{ textDecoration: 'none', color: v('faint') }}>*</abbr> : null}
        </span>
    );
}

/* ----------------------------------------------------------------- métricas soltas */

function Grid({ keys, holder, derived, fmt }: { keys: string[]; holder: MetricHolder; derived: boolean; fmt: Fmtr }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(104px,1fr))', gap: '10px 14px' }}>
            {keys.map((k) => (
                <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                    <span title={M[k].d} style={{ fontSize: 10.5, color: v('muted'), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {M[k].l}
                    </span>
                    <Value k={k} holder={holder} derived={derived} fmt={fmt} size={15} />
                </div>
            ))}
        </div>
    );
}

/* ----------------------------------------------------------------- métricas em faixas */

/** Uma faixa por bloco, na cor do bloco: o primeiro número grande, os outros em linha. */
function Bands({ keys, holder, derived, fmt }: { keys: string[]; holder: MetricHolder; derived: boolean; fmt: Fmtr }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {BLOCKS.map((block) => {
                const ks = keys.filter((k) => M[k].block === block);
                if (!ks.length) return null;
                const [first, ...rest] = ks;
                return (
                    <div key={block} style={{
                        border: `1px solid ${v('border-soft')}`, borderTop: `2px solid ${c(BLOCK_HUE[block])}`,
                        borderRadius: 10, padding: '9px 11px', background: v('inset'),
                        display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0,
                    }}>
                        <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: c(BLOCK_HUE[block]) }}>
                            {BLOCK_LABEL[block]}
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 11.5, color: v('muted') }}>{M[first].l}</span>
                            <Value k={first} holder={holder} derived={derived} fmt={fmt} size={18} />
                        </div>
                        {rest.map((k) => (
                            <div key={k} style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0 }}>
                                <span style={{ fontSize: 11.5, color: v('muted'), flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {M[k].l}
                                </span>
                                <Value k={k} holder={holder} derived={derived} fmt={fmt} size={12.5} />
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

/* ----------------------------------------------------------------- funil de entrega */

function FunnelBars({ holder, fmt }: { holder: MetricHolder; fmt: Fmtr }) {
    // bloco de envios nulo é "não dá para saber", e barra nenhuma seria mentira de precisão
    if (!holder.sends) {
        return (
            <p style={{ margin: 0, fontSize: 11.5, color: v('faint'), lineHeight: 1.5 }}>
                Sem contagem de envio nesta linha — o funil não tem o que desenhar.
            </p>
        );
    }
    const sent = readMetric(holder, 'sent') ?? 0;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {FUNNEL_KEYS.map((k) => {
                const met = M[k];
                const n = readMetric(holder, k);
                const width = sent > 0 && n != null ? Math.min(100, (n / sent) * 100) : 0;
                return (
                    <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                        <span style={{ fontSize: 11, color: v('text-3'), width: 92, flexShrink: 0 }}>{met.l}</span>
                        <span style={{ flex: 1, height: 7, borderRadius: 4, background: v('inset'), minWidth: 0 }}>
                            <span style={{ display: 'block', height: '100%', width: `${width}%`, borderRadius: 4, background: c(met.hue ?? 'blue') }} />
                        </span>
                        <span className="rv-mono" style={{ fontSize: 11.5, fontVariantNumeric: 'tabular-nums', width: 62, textAlign: 'right', color: n == null ? v('faint') : v('text-2') }}>
                            {fmt.metric(met, n)}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

/* ----------------------------------------------------------------- chips de taxa */

function RateChips({ holder, derived, fmt }: { holder: MetricHolder; derived: boolean; fmt: Fmtr }) {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {RATE_KEYS.map((k) => {
                const met = M[k];
                const value = readMetric(holder, k);
                const mark = value != null && derived;
                return (
                    <span key={k} title={`${met.d} — ${met.den}`} style={{
                        display: 'inline-flex', alignItems: 'baseline', gap: 6, height: 24, padding: '0 9px',
                        borderRadius: 7, background: v('chip'), border: `1px solid ${v('border-2')}`, fontSize: 11.5,
                    }}>
                        <span style={{ color: c(met.hue ?? 'blue') }}>{met.l}</span>
                        <span className="rv-mono" style={{ fontVariantNumeric: 'tabular-nums', color: value == null ? v('faint') : v('text') }}>
                            {fmt.metric(met, value)}{mark ? '*' : ''}
                        </span>
                    </span>
                );
            })}
        </div>
    );
}
